// components/consultation-cta.tsx
"use client";

import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface ConsultationCTAProps {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonUrl?: string;
  backgroundVariant?: "default" | "dark" | "teal";
}

export function ConsultationCTA({
  title = "Want to 'howthef' transform your business with AI?",
  description = "Explore Fluxos.ai's consulting services and see how we can build real solutions for your industry.",
  buttonText = "Get a Consultation",
  buttonUrl = "/contact",
  backgroundVariant = "default"
}: ConsultationCTAProps) {
  // Background variants
  const bgClasses = {
    default: "bg-gradient-to-r from-bg-dark to-bg-slate",
    dark: "bg-black",
    teal: "bg-[#1E2D30]"
  };

  return (
    <div className={`mt-12 p-8 ${bgClasses[backgroundVariant]} rounded-lg`}>
      <div className="md:flex items-center justify-between">
        <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
          <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>
          <p className="text-light-gray mb-6">
            {description}
          </p>
        </div>
        <div className="md:w-1/3 flex justify-center">
          <Button asChild className="bg-teal hover:bg-teal/80 text-black font-bold py-3 px-6 text-lg">
            <Link href={buttonUrl}>
              {buttonText}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

interface RelatedCaseProps {
  title: string;
  slug: string;
  category: string;
  model: string;
  focus: string;
  image?: string;
}

function RelatedCaseCard({ title, slug, category, model, focus, image }: RelatedCaseProps) {
  return (
    <Link href={`/use-cases/${slug}`} className="group">
      <div className="bg-bg-dark rounded-lg overflow-hidden border border-medium-gray transition-all duration-300 h-full hover:border-teal hover:shadow-lg hover:shadow-teal/5">
        {image && (
          <div className="relative h-40 w-full">
            <Image 
              src={image} 
              alt={title}
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60" />
          </div>
        )}
        
        <div className="p-5">
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className="bg-dark-gray text-white hover:bg-dark-gray/90">{category}</Badge>
            <Badge className="bg-teal text-black hover:bg-teal/90">{model}</Badge>
          </div>
          
          <h3 className="text-lg font-display font-bold text-white group-hover:text-teal transition-colors mb-2">{title}</h3>
          <p className="text-light-gray text-sm">{focus}</p>
          
          <div className="flex items-center text-teal mt-4 text-sm font-medium">
            <span className="mr-1">Read case study</span>
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}

interface RelatedUseCasesProps {
  cases: Array<{
    title: string;
    slug: string;
    category: string;
    model: string;
    focus: string;
    image?: string;
  }>;
}

export function RelatedUseCases({ cases }: RelatedUseCasesProps) {
  return (
    <div className="my-12">
      <h2 className="text-2xl font-display font-bold text-white mb-6">
        More 'Howthef' Use Cases
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cases.map((useCase, index) => (
          <RelatedCaseCard
            key={index}
            title={useCase.title}
            slug={useCase.slug}
            category={useCase.category}
            model={useCase.model}
            focus={useCase.focus}
            image={useCase.image || `/placeholder-${(index % 3) + 1}.jpg`}
          />
        ))}
      </div>
      
      <div className="mt-8 text-center">
        <Button asChild variant="outline" className="bg-transparent border-teal text-teal hover:bg-teal/10">
          <Link href="/use-cases">
            View all use cases
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

interface FeaturedCaseStudyProps {
  title: string;
  slug: string;
  categories: string[];
  models: string[];
  summary: string;
  problem: string;
  solution: string;
  image?: string;
  readTime?: string;
}

export function FeaturedCaseStudy({
  title,
  slug,
  categories,
  models,
  summary,
  problem,
  solution,
  image = "/placeholder.jpg",
  readTime = "10 min"
}: FeaturedCaseStudyProps) {
  return (
    <Link href={`/use-cases/${slug}`} className="group">
      <div className="overflow-hidden border-0 bg-[#1A2526] hover:bg-[#1E2C2E] transition-colors rounded-xl">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
          <div className="p-6 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {categories.map((category, index) => (
                  <Badge key={index} className="bg-[#2C3839] text-white hover:bg-[#2C3839]/90">
                    {category}
                  </Badge>
                ))}
                
                {models.map((model, index) => (
                  <Badge key={index} className="bg-teal text-black hover:bg-teal/90">
                    {model}
                  </Badge>
                ))}
              </div>
              
              <h3 className="text-2xl font-bold text-white">{title}</h3>
              
              <p className="text-light-gray">{summary}</p>
            </div>

            <div className="mt-6">
              <div className="mb-4 bg-[#212F31] p-4 rounded-md border-l-4 border-teal">
                <span className="text-teal font-semibold">The Problem:</span>
                <p className="text-light-gray mt-1">{problem}</p>
              </div>

              <div className="mb-4">
                <span className="text-teal font-semibold">The Solution:</span>
                <p className="text-light-gray mt-1">{solution}</p>
              </div>

              <div className="flex items-center text-white mt-6 group-hover:text-teal transition-colors">
                <span className="mr-2 font-medium">Read full case study</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>

          <div className="relative h-[300px] lg:h-auto">
            <Image
              src={image}
              alt={title}
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A2526] to-transparent lg:bg-gradient-to-l opacity-60" />
            
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#1A2526] to-transparent">
              <Badge className="bg-teal text-black font-semibold">
                {readTime} read
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
// // components/consultation-cta.tsx
// import { Button } from "@/components/ui/button";

// interface ConsultationCTAProps {
//   title?: string;
//   description?: string;
//   buttonText?: string;
//   buttonUrl?: string;
// }

// export function ConsultationCTA({
//   title = "Want to 'howthef' transform your business with AI?",
//   description = "Explore Fluxos.ai's consulting services and see how we can build real solutions for your industry.",
//   buttonText = "Get a Consultation",
//   buttonUrl = "/contact",
// }: ConsultationCTAProps) {
//   return (
//     <div className="mt-12 p-8 bg-gradient-to-r from-bg-dark to-bg-slate rounded-lg">
//       <div className="md:flex items-center justify-between">
//         <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
//           <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>
//           <p className="text-light-gray mb-6">
//             {description}
//           </p>
//         </div>
//         <div className="md:w-1/3 flex justify-center">
//           <Button className="bg-teal hover:bg-teal/80 text-black font-bold py-3 px-6 text-lg">
//             {buttonText}
//           </Button>
//         </div>
//       </div>
//     </div>
//   );
// }